import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { API_ENDPOINT } from '../constants';
import { fetchWrapper } from '../helpers';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { IServiceCode } from '../utils/types';

const name = 'service_codes';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<ServiceCodeState>) =>
    createExtraReducers(builder),
});

export interface ServiceCodeState {
  list: Array<IServiceCode>;
  error: any;
  loading: boolean;
  // Id of the most recently dispatched getList — the list is query-scoped (callers pass
  // different `is_timesheet_usable` filters), so an older in-flight request resolving after a
  // newer one must not overwrite the newer result. Latest request wins.
  requestId: string | null;
}

function createInitialState(): ServiceCodeState {
  return {
    list: [],
    loading: false,
    error: null,
    requestId: null,
  };
}

function createReducers() {
  return {};
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/service_codes`;

  return {
    getList: getList(),
  };

  function getList() {
    return createAsyncThunk(
      `${name}/getList`,
      async ({ getAccessTokenSilently, params = {} }: any) => {
        const url = `${baseUrl}/?${new URLSearchParams(params)}`;
        const response = await fetchWrapper.get({
          url,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<ServiceCodeState>) {
  const { getList } = extraActions;

  builder
    .addCase(
      getList.pending,
      (state: ServiceCodeState, action: { meta: { requestId: string } }) => {
        state.requestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      },
    )
    .addCase(
      getList.fulfilled,
      (
        state: ServiceCodeState,
        action: PayloadAction<IServiceCode[], string, { requestId: string }>,
      ) => {
        // Drop a stale response from an earlier (differently-filtered) request.
        if (action.meta.requestId !== state.requestId) return;
        state.list = action.payload;
        state.loading = false;
      },
    )
    .addCase(getList.rejected, (state: ServiceCodeState, action: any) => {
      if (action.meta.requestId !== state.requestId) return;
      state.list = [];
      state.error = action.error;
      state.loading = false;
    });
}

export const serviceCodeActions = { ...slice.actions, ...extraActions };
export const serviceCodeReducer = slice.reducer;
